// Which sidebar link is "the page on screen".
//
// Seller Center (mdf-seller-fn) lights a leaf with router-link's own `.active`
// class (router.js `linkActiveClass: "active"`): vue-router 3 inclusive match,
// i.e. the link path is a path-segment prefix of the route (it appends "/" to
// both before comparing) and the query is ignored unless the link carries one.
// So /backoffice/orders?x=y and /seller/products/123/edit light
// /backoffice/orders and /seller/products. That rule is kept here, with two
// corrections for BO's menu:
//   - several leaves can match at once — /wms/inventory and
//     /wms/inventory/summary, /backoffice/orders and /backoffice/orders/create —
//     and router-link lights them all; here only the most specific one (longest
//     path, then most query keys) is active, so exactly one row is lit;
//   - a group header used a raw `startsWith` on its children's paths, so
//     /backoffice/orders-overview lit the group holding /backoffice/orders; a group
//     is now active only through an active descendant (SidebarItem.isActive).

function splitPath(raw) {
  const s = String(raw || '');
  const hashAt = s.indexOf('#');
  const noHash = hashAt === -1 ? s : s.slice(0, hashAt);
  const qAt = noHash.indexOf('?');
  let path = qAt === -1 ? noHash : noHash.slice(0, qAt);
  const search = qAt === -1 ? '' : noHash.slice(qAt + 1);
  if (path.length > 1) path = path.replace(/\/+$/, '');
  if (path === '') path = '/';
  const query = {};
  search.split('&').filter(Boolean).forEach((pair) => {
    const eq = pair.indexOf('=');
    const k = decodeURIComponent(eq === -1 ? pair : pair.slice(0, eq));
    const v = eq === -1 ? '' : decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' '));
    query[k] = v;
  });
  return { path, query };
}

function routeParts(route) {
  if (!route) return null;
  if (typeof route === 'string') return splitPath(route);
  const { path } = splitPath(route.path);
  return { path, query: route.query || {} };
}

// How well `linkPath` matches `route`: -1 = no match, otherwise a score where a
// bigger number is a more specific link.
export function matchScore(route, linkPath) {
  const r = routeParts(route);
  if (!r || !linkPath) return -1;
  const l = splitPath(linkPath);
  const prefixOk =
    r.path === l.path ||
    (l.path !== '/' && r.path.indexOf(l.path + '/') === 0);
  if (!prefixOk) return -1;
  const keys = Object.keys(l.query);
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (!(k in r.query)) return -1;
    if (l.query[k] !== '' && String(r.query[k]) !== l.query[k]) return -1;
  }
  return l.path.length * 100 + keys.length;
}

export function matchesRoute(route, linkPath) {
  return matchScore(route, linkPath) >= 0;
}

// The single most specific path among `paths` for `route`, or null.
export function bestMatch(route, paths) {
  let best = null;
  let bestScore = -1;
  (paths || []).forEach((p) => {
    const s = matchScore(route, p);
    if (s > bestScore) {
      bestScore = s;
      best = p;
    }
  });
  return best;
}

// Is the link at `ownPath` the active one, given every leaf path the sidebar
// renders (`allPaths`, may include ownPath)? Ties (the same path in two rows)
// are both active; BO's pinned section MOVES a menu rather than copying it,
// so that does not arise in practice.
export function isLeafActive(route, ownPath, allPaths) {
  const own = matchScore(route, ownPath);
  if (own < 0) return false;
  const others = allPaths || [];
  for (let i = 0; i < others.length; i++) {
    if (matchScore(route, others[i]) > own) return false;
  }
  return true;
}
